// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

// Defines the Navigation component for navigating between pages using react-router.

import React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";

import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";

// The LinkContainer is used to wrap Components that change the URL,
// hooking them up with the Router.
import { LinkContainer } from "react-router-bootstrap";
import { GlobalState } from "../types/stateTypes";
import { buildLiftingLocationSearch } from "../wrpf/live/browserPlatformLiveClient";
import { PlatformLiveProfile } from "../wrpf/live/platformLiveProfile";
import { isWrpfChromeFreePath } from "../wrpf/isWrpfChromeFreePath";
import WebBackupButton from "../wrpf/handoff/WebBackupButton";

const isRootPathActive = (_match: unknown, location: { pathname: string; search: string }): boolean => {
  return location.pathname === "/" && location.search === "";
};

const isLiftingPathActive = (_match: unknown, location: { pathname: string; search: string }): boolean => {
  return (
    location.pathname === "/lifting" ||
    (location.pathname === "/" && new URLSearchParams(location.search).has("lifting"))
  );
};

const isRecordsPathActive = (_match: unknown, location: { pathname: string; search: string }): boolean =>
  location.pathname === "/records" ||
  (location.pathname === "/" && new URLSearchParams(location.search).has("records"));

interface StateProps {
  activeProfile: PlatformLiveProfile | null;
}

const Navigation = ({ activeProfile }: StateProps) => {
  const location = useLocation();
  const liftingTarget =
    activeProfile !== null
      ? { pathname: "/", search: buildLiftingLocationSearch(activeProfile, window.location.origin) }
      : "/lifting";

  if (isWrpfChromeFreePath(location.pathname)) {
    return null;
  }

  return (
    <Navbar sticky="top" bg="dark" variant="dark" expand="lg">
      <Navbar.Brand>
        <img alt="OpenLifter" src="openlifter-white.svg" height="20" style={{ verticalAlign: "middle" }} />
        <img
          alt="WRPF Argentina"
          src="wrpfarg-logo.webp"
          height="20"
          style={{ marginLeft: "0.5rem", verticalAlign: "middle" }}
        />
      </Navbar.Brand>

      {/* Navbar uses Toggle and Collapse to automatically create a hamburger menu
          in case of overflow on small screens.*/}
      <Navbar.Toggle aria-controls="basic-navbar-nav" />
      <Navbar.Collapse>
        <Nav>
          <LinkContainer to="/" isActive={isRootPathActive}>
            <Nav.Link>
              <FormattedMessage id="nav.home" defaultMessage="Home" />
            </Nav.Link>
          </LinkContainer>
          <LinkContainer to="/meet-setup">
            <Nav.Link>
              <FormattedMessage id="nav.meet-setup" defaultMessage="Meet Setup" />
            </Nav.Link>
          </LinkContainer>
          <LinkContainer to="/registration">
            <Nav.Link>
              <FormattedMessage id="nav.registration" defaultMessage="Registration" />
            </Nav.Link>
          </LinkContainer>
          <LinkContainer to="/weigh-ins">
            <Nav.Link>
              <FormattedMessage id="nav.weigh-ins" defaultMessage="Weigh-ins" />
            </Nav.Link>
          </LinkContainer>
          <LinkContainer to="/flight-order">
            <Nav.Link>
              <FormattedMessage id="nav.flight-order" defaultMessage="Flight Order" />
            </Nav.Link>
          </LinkContainer>
          <LinkContainer to={liftingTarget} isActive={isLiftingPathActive}>
            <Nav.Link>
              <FormattedMessage id="nav.lifting" defaultMessage="Lifting" />
            </Nav.Link>
          </LinkContainer>
          <LinkContainer to="/wrpf/live">
            <Nav.Link>
              <FormattedMessage id="nav.platformLive" defaultMessage="Platform Live" />
            </Nav.Link>
          </LinkContainer>
          <LinkContainer to="/records" isActive={isRecordsPathActive}>
            <Nav.Link>
              <FormattedMessage id="nav.records" defaultMessage="Records" />
            </Nav.Link>
          </LinkContainer>
          <LinkContainer to="/results">
            <Nav.Link>
              <FormattedMessage id="nav.results" defaultMessage="Results" />
            </Nav.Link>
          </LinkContainer>
          {/* No Debug link: its Reset wipes the meet and an operator mistook it for the Tarima en vivo
              restart mid-competition. The page stays reachable from a small "depurar" link in the Home footer. */}
        </Nav>
        <WebBackupButton />
      </Navbar.Collapse>
    </Navbar>
  );
};

const mapStateToProps = (state: GlobalState): StateProps => ({
  activeProfile: state.wrpfLive?.activeProfile ?? null,
});

export default connect(mapStateToProps)(Navigation);
